import type { otaModeFactory } from '@syqel/ota-client'
import type { LifecycleLedger } from './renderPolicy'

type ModeFactory = typeof otaModeFactory
type Mode = ReturnType<ModeFactory>
type Staged = { attachChunked?: (canvas: HTMLCanvasElement, budgetMs: number) => boolean }
type Retiring = { disposeChunked?: (budgetMs: number) => boolean }

/**
 * A VisualHost mode factory whose every renderer reports its CONSTRUCTION and TEARDOWN to `ledger`
 * (renderPolicy.ts LifecycleLedger): its creation, attach, each staged-attach step, its first render (the lazy
 * shader compile), dispose and each dispose step mark their frame; a renderer still building async (isReady false
 * — a staged attach in flight, a compile settling in a timer) is watched until it is ready or disposed. The
 * governor skips those frames: a world's build is the host's cost, never the cost of the world being measured.
 * Every web surface that calibrates detail builds its host on this (the TV receivers, the phone surface, the
 * web + desktop Visualizer).
 */
export function withLifecycle(factory: ModeFactory, ledger: LifecycleLedger): ModeFactory {
  return (world) => {
    const mode: Mode = ledger.work(() => factory(world))
    const watched = mode as { isReady?: boolean }
    ledger.watch(watched)
    const attach = mode.attach.bind(mode)
    const dispose = mode.dispose.bind(mode)
    const render = mode.render.bind(mode)
    mode.attach = (canvas) => ledger.work(() => attach(canvas))
    const staged = mode as Staged
    const attachChunked = staged.attachChunked
    if (typeof attachChunked === 'function') staged.attachChunked = (canvas, budgetMs) => ledger.work(() => attachChunked.call(mode, canvas, budgetMs))
    let firstRender = true
    mode.render = () => {
      if (!firstRender) return render()
      firstRender = false
      return ledger.work(() => render())
    }
    mode.dispose = () => { ledger.forget(watched); return ledger.work(() => dispose()) }
    const retiring = mode as Retiring
    const disposeChunked = retiring.disposeChunked
    if (typeof disposeChunked === 'function') retiring.disposeChunked = (budgetMs) => { ledger.forget(watched); return ledger.work(() => disposeChunked.call(mode, budgetMs)) }
    return mode
  }
}
